<script setup>
import { computed, reactive, ref, nextTick } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import FormInput from 'v3/components/Form/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import LabelEmojiPicker from './LabelEmojiPicker.vue';
import {
  LABEL_COLOR_SWATCHES,
  pickRandomLabelSwatch,
} from 'dashboard/helper/labelSwatches';
import validations, { getLabelTitleErrorMessage } from './validations';
import { normalizeLabelTitle } from './labelTitleNormalize';

const props = defineProps({
  prefillTitle: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();
const dialogRef = ref(null);

const uiFlags = useMapGetter('labels/getUIFlags');

const form = reactive({
  title: '',
  description: '',
  color: pickRandomLabelSwatch(),
  showOnSidebar: true,
  emoji: '',
});

const v$ = useVuelidate(validations, form);

const labelTitleErrorMessage = computed(() => {
  const msg = getLabelTitleErrorMessage(v$.value);
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -- key from validation helper
  return msg ? t(msg) : '';
});

const previewText = computed(
  () => form.title || t('LABEL_MGMT.FORM.NAME.PLACEHOLDER')
);

function resetForm() {
  form.title = '';
  form.description = '';
  form.color = pickRandomLabelSwatch();
  form.showOnSidebar = true;
  form.emoji = '';
}

function handleDialogClose() {
  resetForm();
  v$.value.$reset();
  emit('close');
}

function open() {
  resetForm();
  form.title = normalizeLabelTitle(props.prefillTitle || '');
  nextTick(() => {
    v$.value.$reset();
  });
  dialogRef.value?.open();
}

function close() {
  dialogRef.value?.close();
}

defineExpose({ open, close });

async function submit() {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    await store.dispatch('labels/create', {
      title: normalizeLabelTitle(form.title),
      description: form.description,
      color: form.color,
      show_on_sidebar: form.showOnSidebar,
      emoji: form.emoji,
    });
    useAlert(t('LABEL_MGMT.ADD.API.SUCCESS_MESSAGE'));
    close();
  } catch (error) {
    const errorMessage =
      error?.message || t('LABEL_MGMT.ADD.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
}
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('LABEL_MGMT.ADD.TITLE')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    overflow-y-auto
    width="xl"
    @close="handleDialogClose"
  >
    <form class="grid w-full gap-3" @submit.prevent.stop="submit">
      <div
        class="inline-flex w-fit max-w-full shrink-0 items-center gap-1.5 self-start rounded-lg px-3 py-1.5 text-xs font-semibold outline outline-1 -outline-offset-1"
        :style="{
          backgroundColor: `${form.color}26`,
          outlineColor: form.color,
        }"
      >
        <span v-if="form.emoji" class="text-base leading-none">{{
          form.emoji
        }}</span>
        <span class="truncate text-n-slate-12">{{ previewText }}</span>
      </div>

      <div class="flex min-w-0 flex-col gap-1">
        <span class="text-sm font-medium leading-6 text-n-slate-12">
          {{ t('LABEL_MGMT.FORM.NAME.LABEL') }}
        </span>
        <div class="grid min-w-0 grid-cols-[3.5rem_1fr] items-stretch gap-2">
          <LabelEmojiPicker v-model="form.emoji" hide-label />
          <div class="mt-0.5 min-w-0">
            <FormInput
              v-model="form.title"
              name="label-title"
              spacing="compact"
              label=""
              :placeholder="t('LABEL_MGMT.FORM.NAME.PLACEHOLDER')"
              :has-error="v$.title.$error"
              :error-message="labelTitleErrorMessage"
              data-testid="label-title"
              @blur="
                form.title = normalizeLabelTitle(form.title);
                v$.title.$touch();
              "
            />
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <span class="text-sm font-medium leading-6 text-n-slate-12">
          {{ t('LABEL_MGMT.FORM.COLOR.LABEL') }}
        </span>
        <div class="flex flex-wrap gap-1.5">
          <button
            v-for="swatch in LABEL_COLOR_SWATCHES"
            :key="swatch"
            type="button"
            class="relative flex size-7 shrink-0 items-center justify-center rounded-full transition-all"
            :class="{
              'outline outline-1 outline-offset-1 outline-transparent':
                form.color !== swatch,
            }"
            :style="
              form.color === swatch
                ? {
                    backgroundColor: swatch,
                    boxShadow: `0 0 0 2px rgb(255 255 255), 0 0 0 4px ${swatch}`,
                  }
                : { backgroundColor: swatch }
            "
            :aria-pressed="form.color === swatch"
            @click="form.color = swatch"
          >
            <span
              v-if="form.color === swatch"
              class="pointer-events-none i-lucide-check size-4 shrink-0 text-white drop-shadow-[0_0_1px_rgb(0_0_0_/_0.95)] drop-shadow-[0_1px_2px_rgb(0_0_0_/_0.65)]"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-1">
          <span class="text-sm font-medium leading-6 text-n-slate-12">
            {{ t('LABEL_MGMT.FORM.DESCRIPTION.LABEL') }}
          </span>
          <Icon
            v-tooltip.top="t('LABEL_MGMT.FORM.DESCRIPTION.HINT')"
            icon="i-lucide-info"
            class="size-3.5 shrink-0 cursor-help text-n-slate-10"
          />
        </div>
        <TextArea
          v-model="form.description"
          :placeholder="t('LABEL_MGMT.FORM.DESCRIPTION.PLACEHOLDER')"
          :max-length="2000"
          min-height="3.25rem"
          resize
          custom-text-area-wrapper-class="[&_textarea]:rounded-lg"
          data-testid="label-description"
        />
      </div>

      <label class="flex cursor-pointer items-center gap-2">
        <input
          v-model="form.showOnSidebar"
          type="checkbox"
          class="size-4 shrink-0 rounded border-n-weak text-n-brand"
        />
        <span class="text-sm text-n-slate-12">{{
          t('LABEL_MGMT.FORM.SHOW_ON_SIDEBAR.LABEL')
        }}</span>
      </label>
    </form>

    <template #footer>
      <div
        class="flex w-full flex-wrap items-center justify-end gap-3 border-t border-n-weak pt-3"
      >
        <Button
          sm
          variant="link"
          slate
          type="button"
          class="!font-bold"
          :label="t('LABEL_MGMT.FORM.CANCEL')"
          @click="close"
        />
        <Button
          sm
          blue
          type="button"
          class="rounded-lg !font-bold"
          :label="t('LABEL_MGMT.FORM.CREATE')"
          :disabled="v$.$invalid || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
          @click="submit"
        />
      </div>
    </template>
  </Dialog>
</template>
